// 导入必要的 hooks 和组件
import { usePrefixCls } from '../../hook/useConfig';
import { useLocale } from '../../locales';
import LoginInput from '../../components/input/loginInput';
import { InputStatus } from '../../enum/common';
// 导入 Semi Design 图标
import {
  IconUser,
  IconTickCircle,
  IconClear,
  IconLockStroked,
  IconUploadError,
} from '@douyinfe/semi-icons';
// 导入 React hooks 和 lodash 工具函数
import { useState, startTransition } from 'react';
import { cloneDeep } from 'lodash';
// 导入类型定义
import { LoginInputRenderProps } from '../../components/input/types';
// 导入 Semi Design 组件
import { Button, Toast } from '@douyinfe/semi-ui'; // 确保导入 Toast 和 Checkbox
// 导入状态管理 store
import useStore from '../../store';
import useUserStore from '../../store/user';
// 导入配置和 API
import { webSettings } from '../../config/setting';
import { loginUser } from '../../api/login';
// 导入路由 hook
import { useNavigate } from 'react-router-dom';
// 导入加密库
import CryptoJS from 'crypto-js'; // 导入 crypto-js

// 登录表单组件
const LoginForm = () => {
  // 获取 CSS 类前缀
  const prefixCls = usePrefixCls('login-main-body');
  // 获取国际化文本格式化函数
  const { getFormatText } = useLocale();
  // 获取全局 store 中的方法
  const { setToken } = useStore();
  // 从store中获取业务逻辑函数
  const { fetchUserDetails, fetchSubAccountList } = useUserStore();

  // 显示错误信息
  const showError = (messageKey: string) => {
    Toast.error(getFormatText(messageKey));
  };
  // 控制登录按钮加载状态
  const [loginLoading, setLoading] = useState(false);
  // 路由导航 hook
  const navigate = useNavigate();
  // 输入框状态对应的图标映射
  const iconMap = {
    [InputStatus.wait]: '',
    [InputStatus.success]: (
      <IconTickCircle style={{ color: 'var(--semi-color-success)' }} />
    ),
    [InputStatus.error]: (
      <IconClear style={{ color: 'var(--semi-color-danger)' }} />
    ),
    [InputStatus.warning]: (
      <IconUploadError style={{ color: 'var(--semi-color-warning)' }} />
    ),
  };
  // 表单输入框状态控制
  const [statusMap] = useState({
    username: InputStatus.wait,
    password: InputStatus.wait,
  });

  // 表单数据状态，初始化用户名和密码为空
  const [formData, setFormData] = useState({ username: '', password: '' }); // 修改此处，密码初始为空

  // 更新表单数据
  function changeFormData(field: 'username' | 'password', value: string) {
    const data = cloneDeep(formData);
    data[field] = value;
    setFormData(data);
  }

  // 渲染输入框列表
  function renderInputs() {
    // 定义输入框配置列表
    const inputList: LoginInputRenderProps[] = [
      {
        type: 'username',
        icon: <IconUser size={'inherit'} />,
        status: statusMap.username,
        placeholder: getFormatText('web.login.userName'),
        defaultValue: '', // 用户名可以有默认值
      },
      {
        type: 'password',
        icon: <IconLockStroked size={'inherit'} />,
        enterEvent: submitForm,
        status: statusMap.password,
        placeholder: getFormatText('web.login.password'),
        defaultValue: '', // 密码输入框的默认值也应为空
      },
    ];
    // 遍历配置列表渲染 LoginInput 组件
    return inputList.map((input) => {
      return (
        <LoginInput
          key={input.type}
          changeData={(value: string) => changeFormData(input.type, value)}
          mode={input.type === 'password' ? 'password' : undefined}
          icon={input.icon}
          enterEvent={input.enterEvent}
          status={input.status}
          suffix={iconMap[input.status]}
          placeholder={input.placeholder}
          defaultValue={input.defaultValue}
        />
      );
    });
  }

  // 验证表单数据
  const validateForm = (): boolean => {
    if (!formData.username) {
      showError('web.login.errorUserNameNull');
      return false;
    }
    if (!formData.password) {
      showError('web.login.errorPasswordNull');
      return false;
    }
    return true;
  };

  const submitForm = async () => {
    // 表单验证
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // 密码加密
      const encryptedPassword = CryptoJS.MD5(formData.password).toString();

      // 执行登录
      const loginResponse = await loginUser({
        username: formData.username,
        password: encryptedPassword,
      });

      const { data: loginData } = loginResponse;
      // 检查登录响应
      if (!loginData?.token) {
        Toast.error(loginResponse?.msg || '登录失败，请检查账号密码是否正确');
        return;
      }

      // 保存 token
      setToken(loginData.token);

      // 并行获取用户信息和子账号列表
      const [userDetailsSuccess] = await Promise.allSettled([
        fetchUserDetails(loginData.id, undefined, showError),
        fetchSubAccountList(showError),
      ]);

      // 检查用户详情获取是否成功
      if (
        userDetailsSuccess.status === 'rejected' ||
        !userDetailsSuccess.value
      ) {
        console.warn('用户详情获取失败，但登录成功');
      }

      // 导航到默认路由
      startTransition(() => {
        navigate(webSettings.defaultRouter);
      });
    } catch (error) {
      console.error('登录过程中发生错误:', error);
      Toast.error('登录失败，请检查您的凭据');
    } finally {
      setLoading(false);
    }
  };

  return (
    // 登录表单容器
    <div className={`${prefixCls}-form flex flex-col`}>
      {/* 表单主体 */}
      <div className={`${prefixCls}-form-body`}>
        {/* 渲染输入框 */}
        {renderInputs()}

        {/* 操作区域：记住我和忘记密码 */}
        {/* <div className={`${prefixCls}-form-body-operate`}>
          <div className='remember-me'>
            <Checkbox>记住密码</Checkbox>
          </div>
          <a href='#' className='forgot-password'>
            忘记密码
          </a>
        </div> */}

        {/* 表单底部菜单区域 */}
        <div className={`${prefixCls}-form-body-menu mb-4`}>
          {/* 登录按钮 */}
          <Button
            loading={loginLoading}
            theme='solid'
            size='large'
            onClick={submitForm}
          >
            {getFormatText('web.login.loginText')}
          </Button>
        </div>

        {/* 注册链接 */}
        {/* <div className={`${prefixCls}-form-body-join`}>
          还没有账号？<a href='#'>注册</a>
        </div> */}
      </div>
    </div>
  );
};
// 导出 LoginForm 组件
export default LoginForm;
